import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";
import StaticAreaSeries from "./StaticAreaSeries";
import BarSeries from "./BarSeries";

const normalizeStroke = (stroke, fallback) => ({
  color: stroke?.color || fallback.color,
  dash: stroke?.dash || fallback.dash,
  width: stroke?.width ?? fallback.width,
  opacity: stroke?.opacity ?? fallback.opacity,
});

const FALLBACK_PLUS = { color: "#00FF00", dash: "Solid", width: 1, opacity: 1 };
const FALLBACK_MINUS = {
  color: "#FF0000",
  dash: "Solid",
  width: 1,
  opacity: 1,
};
const FALLBACK_ADX = { color: "#FFD700", dash: "Solid", width: 1, opacity: 1 };
const SHADE_EPSILON = 1e-6;

class ADXSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorPlusDI = this.yAccessorPlusDI.bind(this);
    this.yAccessorMinusDI = this.yAccessorMinusDI.bind(this);
    this.yAccessorADX = this.yAccessorADX.bind(this);
    this.yAccessorHistogram = this.yAccessorHistogram.bind(this);
    this.yAccessorShadingPositive = this.yAccessorShadingPositive.bind(this);
    this.yAccessorShadingNegative = this.yAccessorShadingNegative.bind(this);
    this.baseAtZero = this.baseAtZero.bind(this);
    this.shadingBasePositive = this.shadingBasePositive.bind(this);
    this.shadingBaseNegative = this.shadingBaseNegative.bind(this);
    this.isFiniteValue = this.isFiniteValue.bind(this);
  }

  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    if (!indicator || !indicator.renderKey) {
      return undefined;
    }

    const appearanceConfig = indicator.appearanceConfig || {};
    const showSeries =
      appearanceConfig.showSeries !== undefined
        ? appearanceConfig.showSeries
        : true;
    const showHistogram = !!appearanceConfig.showHistogram;

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let seriesMin = Number.POSITIVE_INFINITY;
    let seriesMax = Number.NEGATIVE_INFINITY;
    let histogramMin = Number.POSITIVE_INFINITY;
    let histogramMax = Number.NEGATIVE_INFINITY;

    const updateExtents = (value, target) => {
      if (!Number.isFinite(value)) return;
      if (value < min) min = value;
      if (value > max) max = value;
      if (target === "series") {
        if (value < seriesMin) seriesMin = value;
        if (value > seriesMax) seriesMax = value;
      }
      if (target === "histogram") {
        if (value < histogramMin) histogramMin = value;
        if (value > histogramMax) histogramMax = value;
      }
    };

    dataArr.forEach((item) => {
      const node = item && item[indicator.renderKey];
      if (!node || typeof node !== "object") return;

      if (showSeries) {
        updateExtents(node.plusDI, "series");
        updateExtents(node.minusDI, "series");
        updateExtents(node.adx, "series");
      }

      if (showHistogram) {
        updateExtents(node.histogram, "histogram");
      }
    });

    return [min, max];
  }

  valueFromDatum(d, key) {
    if (!d) return undefined;
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    const raw = node ? node[key] : undefined;
    return Number.isFinite(raw) ? raw : undefined;
  }

  yAccessorPlusDI(d) {
    return this.valueFromDatum(d, "plusDI");
  }

  yAccessorMinusDI(d) {
    return this.valueFromDatum(d, "minusDI");
  }

  yAccessorADX(d) {
    return this.valueFromDatum(d, "adx");
  }

  yAccessorHistogram(d) {
    return this.valueFromDatum(d, "histogram");
  }

  baseAtZero(xScale, yScale) {
    return yScale(0);
  }

  yAccessorShadingPositive(d) {
    const plus = this.yAccessorPlusDI(d);
    const minus = this.yAccessorMinusDI(d);
    if (!Number.isFinite(plus) || !Number.isFinite(minus)) return undefined;
    if (plus >= minus - SHADE_EPSILON) {
      return plus;
    }
    // collapse area to base when negative zone
    return minus;
  }

  yAccessorShadingNegative(d) {
    const plus = this.yAccessorPlusDI(d);
    const minus = this.yAccessorMinusDI(d);
    if (!Number.isFinite(plus) || !Number.isFinite(minus)) return undefined;
    if (minus > plus + SHADE_EPSILON) {
      return minus;
    }
    return plus;
  }

  shadingBasePositive(yScale, d) {
    const plus = this.yAccessorPlusDI(d);
    const minus = this.yAccessorMinusDI(d);
    if (Number.isFinite(plus) && Number.isFinite(minus)) {
      return yScale(minus);
    }
    return yScale(plus ?? minus ?? 0);
  }

  shadingBaseNegative(yScale, d) {
    const plus = this.yAccessorPlusDI(d);
    const minus = this.yAccessorMinusDI(d);
    if (Number.isFinite(plus) && Number.isFinite(minus)) {
      return yScale(plus);
    }
    return yScale(plus ?? minus ?? 0);
  }

  isFiniteValue(value) {
    return Number.isFinite(value);
  }

  histogramFill(d) {
    const { positiveHistogramColor, negativeHistogramColor } = this.props;
    const value = this.yAccessorHistogram(d);
    if (value == null) return "none";

    const resolveColor = (input, fallback) => {
      if (!input) return fallback;
      if (typeof input === "string") return input;
      if (typeof input === "object" && input.color) return input.color;
      return fallback;
    };

    const positiveColor = resolveColor(positiveHistogramColor, "#00DD00");
    const negativeColor = resolveColor(negativeHistogramColor, "#FF0000");

    return value >= 0 ? positiveColor : negativeColor;
  }

  render() {
    const {
      className,
      clip,
      showSeries,
      showShading,
      showHistogram,
      positiveDIStroke,
      negativeDIStroke,
      adxStroke,
      histogramOpacity,
      shadingOpacity,
      onContextMenu,
      onDoubleClick,
    } = this.props;

    const plusStroke = normalizeStroke(positiveDIStroke, FALLBACK_PLUS);
    const minusStroke = normalizeStroke(negativeDIStroke, FALLBACK_MINUS);
    const adxLineStroke = normalizeStroke(adxStroke, FALLBACK_ADX);

    const positiveFill = plusStroke.color;
    const negativeFill = minusStroke.color;
    const effectiveHistogramOpacity = histogramOpacity ?? 0.6;
    const effectiveShadingOpacity = shadingOpacity ?? 0.15;

    return (
      <g className={className}>
        {showHistogram && (
          <BarSeries
            className="react-stockcharts-adx-histogram"
            clip={clip}
            baseAt={this.baseAtZero}
            stroke={false}
            fill={(d) => this.histogramFill(d)}
            opacity={effectiveHistogramOpacity}
            widthRatio={0.6}
            yAccessor={this.yAccessorHistogram}
            onContextMenu={onContextMenu}
            onDoubleClick={onDoubleClick}
          />
        )}

        {showShading && showSeries && (
          <>
            <StaticAreaSeries
              className="react-stockcharts-adx-shading-positive"
              yAccessor={this.yAccessorShadingPositive}
              base={this.shadingBasePositive}
              defined={this.isFiniteValue}
              fill={positiveFill}
              opacity={effectiveShadingOpacity}
              stroke={{ color: "none", opacity: 0, width: 0, dash: "Solid" }}
            />
            <StaticAreaSeries
              className="react-stockcharts-adx-shading-negative"
              yAccessor={this.yAccessorShadingNegative}
              base={this.shadingBaseNegative}
              defined={this.isFiniteValue}
              fill={negativeFill}
              opacity={effectiveShadingOpacity}
              stroke={{ color: "none", opacity: 0, width: 0, dash: "Solid" }}
            />
          </>
        )}

        {showSeries && (
          <>
            <LineSeries
              className="react-stockcharts-adx-plus"
              clip={clip}
              yAccessor={this.yAccessorPlusDI}
              stroke={plusStroke}
              connectNulls
              highlightOnHover
              onContextMenu={onContextMenu}
              onDoubleClick={onDoubleClick}
            />
            <LineSeries
              className="react-stockcharts-adx-minus"
              clip={clip}
              yAccessor={this.yAccessorMinusDI}
              stroke={minusStroke}
              connectNulls
              highlightOnHover
              onContextMenu={onContextMenu}
              onDoubleClick={onDoubleClick}
            />
            <LineSeries
              className="react-stockcharts-adx-line"
              clip={clip}
              yAccessor={this.yAccessorADX}
              stroke={adxLineStroke}
              connectNulls
              highlightOnHover
              onContextMenu={onContextMenu}
              onDoubleClick={onDoubleClick}
            />
          </>
        )}
      </g>
    );
  }
}

ADXSeries.propTypes = {
  className: PropTypes.string,
  clip: PropTypes.bool,
  yAccessor: PropTypes.func.isRequired,
  showSeries: PropTypes.bool,
  showShading: PropTypes.bool,
  showHistogram: PropTypes.bool,
  positiveDIStroke: PropTypes.shape({
    color: PropTypes.string,
    dash: PropTypes.string,
    width: PropTypes.number,
    opacity: PropTypes.number,
  }),
  negativeDIStroke: PropTypes.shape({
    color: PropTypes.string,
    dash: PropTypes.string,
    width: PropTypes.number,
    opacity: PropTypes.number,
  }),
  adxStroke: PropTypes.shape({
    color: PropTypes.string,
    dash: PropTypes.string,
    width: PropTypes.number,
    opacity: PropTypes.number,
  }),
  positiveHistogramColor: PropTypes.string,
  negativeHistogramColor: PropTypes.string,
  histogramOpacity: PropTypes.number,
  shadingOpacity: PropTypes.number,
  onContextMenu: PropTypes.func,
  onDoubleClick: PropTypes.func,
};

ADXSeries.defaultProps = {
  className: "react-stockcharts-adx-series",
  clip: true,
  showSeries: true,
  showShading: false,
  showHistogram: false,
  positiveHistogramColor: "#00DD00",
  negativeHistogramColor: "#FF0000",
  histogramOpacity: 0.6,
  shadingOpacity: 0.15,
};

export default ADXSeries;
